import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TimeRangeFilter } from '@/components/admin/TimeRangeFilter';
import { Lightbulb, MessageSquare, Target, TrendingUp, Clock, AlertCircle, Calendar } from 'lucide-react';
import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';

interface AdSuggestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSuggestions: (params: {
    clinicaId: string;
    startDate: string;
    endDate: string;
    deliveryMethod: 'system' | 'whatsapp';
    phoneNumber?: string;
  }) => void;
  isCreating: boolean;
  adminMode?: boolean;
  selectedClinica?: { id: string; nome: string; telefone?: string };
  monthlyLimit?: number;
  suggestionsThisMonth?: number;
}

/**
 * Modal para configurar e criar sugestões de anúncios
 * 
 * Este modal permite aos usuários configurar o período de análise
 * e o método de entrega para gerar sugestões de anúncios personalizadas
 * com base nas conversas dos leads. Similar ao AIReportModal.
 */
export const AdSuggestionsModal = ({
  isOpen,
  onClose,
  onCreateSuggestions,
  isCreating,
  adminMode = false,
  selectedClinica,
  monthlyLimit = 999,
  suggestionsThisMonth = 0
}: AdSuggestionsModalProps) => {
  const [deliveryMethod, setDeliveryMethod] = useState<'system' | 'whatsapp'>('system');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Estado do período seguindo o padrão do AIReportModal
  const [selectedPeriod, setSelectedPeriod] = useState<{
    start: Date | null;
    end: Date | null;
    filterName: string;
  }>({
    start: null,
    end: null,
    filterName: 'Últimos 30 dias'
  });

  // Preencher número do telefone da clínica automaticamente
  useEffect(() => {
    if (selectedClinica?.telefone) {
      setPhoneNumber(selectedClinica.telefone);
    }
  }, [selectedClinica]);

  // Handler para mudança de período (seguindo padrão do AIReportModal)
  const handlePeriodChange = (start: Date | null, end: Date | null, filterName: string) => {
    setSelectedPeriod({ start, end, filterName });
  };

  const handleSubmit = () => {
    if (!selectedPeriod.start || !selectedPeriod.end) return;

    const clinicaId = adminMode ? selectedClinica?.id : undefined;
    if (!clinicaId && adminMode) return;

    onCreateSuggestions({
      clinicaId: clinicaId || '',
      startDate: selectedPeriod.start.toISOString(),
      endDate: selectedPeriod.end.toISOString(),
      deliveryMethod,
      phoneNumber: deliveryMethod === 'whatsapp' ? phoneNumber : undefined
    });
  };

  const isFormValid = selectedPeriod.start && selectedPeriod.end && 
    (deliveryMethod === 'system' || (deliveryMethod === 'whatsapp' && phoneNumber.trim()));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            Gerar Sugestões de Anúncios
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações sobre o processo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Target className="w-4 h-4" />
                Como funciona a análise
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 space-y-2">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 mt-0.5 text-blue-500" />
                <span>Nossa IA analisa as conversas do período selecionado para identificar padrões, dores e desejos dos leads</span>
              </div>
              <div className="flex items-start gap-2">
                <TrendingUp className="w-4 h-4 mt-0.5 text-green-500" />
                <span>Gera sugestões personalizadas de anúncios, ganchos, headlines e roteiros baseados nos dados reais</span>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-purple-500" />
                <span>O processamento leva alguns minutos e você será notificado quando estiver pronto</span>
              </div>
            </CardContent>
          </Card>

          {/* Limite mensal */}
          {!adminMode && monthlyLimit < 999 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    Uso mensal: {suggestionsThisMonth}/{monthlyLimit} sugestões utilizadas
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Seleção de Período - CORRIGIDO seguindo o padrão do AIReportModal */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <Label className="text-base font-medium">Período da Análise</Label>
                </div>
                
                <TimeRangeFilter 
                  onFilterChange={handlePeriodChange}
                  currentFilter={selectedPeriod.filterName}
                />

                {selectedPeriod.start && selectedPeriod.end && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      Analisando dados de {selectedPeriod.start.toLocaleDateString('pt-BR')} até {selectedPeriod.end.toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Método de entrega */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Método de entrega</Label>
            <RadioGroup value={deliveryMethod} onValueChange={(value: 'system' | 'whatsapp') => setDeliveryMethod(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="system" id="system" />
                <Label htmlFor="system" className="cursor-pointer">
                  Visualizar no sistema
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="whatsapp" id="whatsapp" />
                <Label htmlFor="whatsapp" className="cursor-pointer">
                  Enviar por WhatsApp
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Campo de telefone para WhatsApp */}
          {deliveryMethod === 'whatsapp' && (
            <div>
              <Label htmlFor="phone" className="text-sm font-medium">
                Número do WhatsApp
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(00) 00000-0000"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Digite o número com DDD para receber as sugestões
              </p>
            </div>
          )}

          {/* Informações da clínica (modo admin) */}
          {adminMode && selectedClinica && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <p className="text-sm text-blue-700">
                  <strong>Clínica selecionada:</strong> {selectedClinica.nome}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Botões de ação */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!isFormValid || isCreating}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {isCreating ? 'Gerando...' : 'Gerar Sugestões'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};